import { useEffect, useRef, useState } from 'react'
import { Sheet } from '../ui/Sheet'

/**
 * Rename one file or folder — owner, 2026-09-14, relaying a user's ask for a rename in
 * the browser's long-press menu.
 *
 * A SHEET rather than an edit-in-place row, for the reason every destructive-ish file
 * action in this app is a sheet: it can name the MACHINE. The browser can be pointed at
 * a mounted remote, and a rename typed into a row gives no clue whose disk it lands on
 * (DiscardConfirm makes the same argument at greater length, and for the same proxy
 * behaviour). It also puts the field somewhere a phone keyboard cannot cover the thing
 * being renamed.
 */
export function RenameSheet({
  name,
  isDir,
  host,
  busy,
  error,
  onCancel,
  onRename,
}: {
  name: string
  isDir: boolean
  /** Mounted remote this will run on; null = Origin. */
  host: string | null
  busy: boolean
  /** The edge's refusal, shown in place rather than as a status line that scrolls
   *  away — "already exists" is an answer to the word still in the field. */
  error: string | null
  onCancel: () => void
  onRename: (to: string) => void
}) {
  const [next, setNext] = useState(name)
  const input = useRef<HTMLInputElement>(null)

  /*
   * Autofocus WITH the stem selected, not the whole name.
   *
   * The repo's default is not to autofocus (AgentCard: an autofocused input raises the
   * keyboard over the content on every phone tap), and the exception it already makes
   * is this exact one — the mkdir field, whose comment reads "autofocus is deliberate:
   * the rename field opens for typing". A sheet the person opened BY CHOOSING RENAME
   * has no other purpose, so the keyboard is what they came for. Selecting only the
   * stem is what makes it usable: renaming `report.pdf` is almost never about `.pdf`,
   * and a leading dot is not an extension, so dotfiles select whole.
   */
  useEffect(() => {
    const el = input.current
    if (!el) return
    el.focus()
    const dot = name.lastIndexOf('.')
    el.setSelectionRange(0, dot > 0 ? dot : name.length)
  }, [name])

  const to = next.trim()
  const ready = to !== '' && to !== name && !busy

  return (
    <Sheet
      title={isDir ? 'Rename folder' : 'Rename file'}
      onClose={onCancel}
      actions={
        <>
          <button type="button" className="sheet-btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="sheet-btn primary"
            disabled={!ready}
            onClick={() => onRename(to)}
          >
            {busy ? 'Renaming…' : 'Rename'}
          </button>
        </>
      }
    >
      <form
        className="fs-rnform"
        onSubmit={(e) => {
          e.preventDefault()
          if (ready) onRename(to)
        }}
      >
        <input
          ref={input}
          value={next}
          onChange={(e) => setNext(e.target.value)}
          aria-label="New name"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
        />
        {/* Same convention as every other prompt that acts on files: say whose disk. */}
        <div className="gittarget">
          <span>{host ? `on ${host}` : 'on Origin (this machine)'}</span>
        </div>
        {error && <p className="fs-rnerr">{error}</p>}
      </form>
    </Sheet>
  )
}
